import { AppStrings } from "../../../utils/constants/AppStrings";
import type {
  TriggerSource,
  FieldOperator,
  DynamicBonusCampaignStatus,
  SegmentStatus,
  DisplayLocation,
} from "./types";

export type SelectOption<T extends string = string> = {
  label: string;
  value: T;
};

export const DEFAULT_STEP_BANNER_COPY_BY_RESULT_KEY: Record<
  string,
  { title?: string; shortDescription?: string }
> = {
  create_profit_boost_promotion: {
    title: "Profit Boost",
    shortDescription: "Use your Profit Boost on a qualifying parlay.",
  },
  award_cashback: {
    title: "Cashback",
    shortDescription: "Cashback on your net losses for this period.",
  },
};

export const supportsStepBannerEditor = (
  resultTypeKey: string,
  resultType?: string,
): boolean =>
  resultType === "offer" ||
  (resultType === "award" && resultTypeKey !== "refund_on_loss");

export const DISPLAY_LOCATIONS: Record<
  Uppercase<DisplayLocation>,
  DisplayLocation
> = {
  SPORTSBOOK: "sportsbook",
  CASINO: "casino",
  DISCOVER: "discover",
  PROFILE: "profile",
  DEPOSIT: "deposit",
};

/** Canonical display order for the per-step display-locations checkboxes (BONENG-921). */
export const DISPLAY_LOCATION_OPTIONS = (): SelectOption<DisplayLocation>[] => [
  {
    label: AppStrings.DISPLAY_LOCATION_SPORTSBOOK,
    value: DISPLAY_LOCATIONS.SPORTSBOOK,
  },
  { label: AppStrings.DISPLAY_LOCATION_CASINO, value: DISPLAY_LOCATIONS.CASINO },
  {
    label: AppStrings.DISPLAY_LOCATION_DISCOVER,
    value: DISPLAY_LOCATIONS.DISCOVER,
  },
  { label: AppStrings.DISPLAY_LOCATION_PROFILE, value: DISPLAY_LOCATIONS.PROFILE },
  { label: AppStrings.DISPLAY_LOCATION_DEPOSIT, value: DISPLAY_LOCATIONS.DEPOSIT },
];

export const TRIGGER_SOURCE_OPTIONS: SelectOption<TriggerSource>[] = [
  { label: "CRM", value: "crm" },
  { label: "EventBridge", value: "eventbridge" },
  { label: "SQS", value: "sqs" },
  { label: "DynamoDB Stream", value: "dynamodb_stream" },
];

/**
 * Canonical trigger keys for legacy segment `membership_rules[].trigger_key`
 * display. Segment authoring now picks catalog `event_key` via
 * `useDynamicBonusEvents` (BONENG-983); this map remains for NL labels when
 * older rules still only carry `trigger_key`.
 */
export const TRIGGER_KEY_OPTIONS = (): SelectOption[] => [
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_SEGMENT_TRIGGER_DEPOSIT_COMPLETED,
    value: "deposit_completed",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_SEGMENT_TRIGGER_BET_SETTLED,
    value: "bet_settled",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_SEGMENT_TRIGGER_USER_SIGNUP,
    value: "users:INSERT",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_SEGMENT_TRIGGER_KYC_VERIFIED,
    value: "kyc.verified",
  },
];

export const segmentTriggerDisplayLabel = (triggerKey: string): string =>
  TRIGGER_KEY_OPTIONS().find((option) => option.value === triggerKey)?.label ??
  triggerKey;

const COMPARE_FIELD_OPERATORS: FieldOperator[] = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
];

/** Segment field-checks — includes `exists` / `in`, which campaign compare does not support. */
export const fieldOperatorOptions = (): SelectOption<FieldOperator>[] => [
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_EQ, value: "eq" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_NE, value: "ne" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_GT, value: "gt" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_GTE, value: "gte" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_LT, value: "lt" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_LTE, value: "lte" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_EXISTS, value: "exists" },
  { label: AppStrings.PAM_SEGMENTS_OPERATOR_IN, value: "in" },
];

/** Campaign `event_field_compare` operators (gte/lte/gt/lt/eq/ne only). */
export const compareFieldOperatorOptions = (): SelectOption<FieldOperator>[] =>
  fieldOperatorOptions().filter((option) =>
    COMPARE_FIELD_OPERATORS.includes(option.value),
  );

export const segmentStatusBadgeTone = (
  status: SegmentStatus,
): "success" | "warning" | "neutral" =>
  status === "active" ? "success" : status === "paused" ? "warning" : "neutral";

export const EMPTY_DISPLAY = "—";

/** Matches `.campaignPreviewBanner` width in stylesDynamicBonuses.module.css. */
export const LIST_TABLE_BANNER_WIDTH = 184;

/** Banner thumbnail width + left pad so the first column isn’t flush with the table edge. */
export const LIST_TABLE_BANNER_COLUMN_WIDTH = LIST_TABLE_BANNER_WIDTH + 12;

/** Enough width for typical campaign / template names without truncating. */
export const LIST_TABLE_NAME_COLUMN_WIDTH = 280;

/** User-facing label for segment membership / rule duration fields (days). */
export const LOOKBACK_WINDOW_DAYS_LABEL =
  AppStrings.PAM_SEGMENTS_LOOKBACK_WINDOW_DAYS;

export type CampaignStatusBadgeProps = {
  tone?: "success" | "danger" | "warning" | "info" | "neutral";
  color?: string;
};

export const campaignStatusBadgeProps = (
  status: DynamicBonusCampaignStatus,
): CampaignStatusBadgeProps => {
  if (status === "expired") return { color: "orange" };
  return {
    tone:
      status === "active"
        ? "success"
        : status === "paused"
          ? "warning"
          : status === "archived"
            ? "danger"
            : "neutral",
  };
};

export const campaignStatusOptions =
  (): SelectOption<DynamicBonusCampaignStatus>[] => [
    { label: AppStrings.PAM_DYNAMIC_BONUS_STATUS_DRAFT, value: "draft" },
    { label: AppStrings.ACTIVE_FILTER_OPTION, value: "active" },
    { label: AppStrings.PAM_SEGMENTS_STATUS_PAUSED, value: "paused" },
    { label: AppStrings.EXPIRED, value: "expired" },
    { label: AppStrings.PAM_SEGMENTS_STATUS_ARCHIVED, value: "archived" },
  ];

export const segmentStatusOptions = (): SelectOption<SegmentStatus>[] => [
  { label: AppStrings.ACTIVE_FILTER_OPTION, value: "active" },
  { label: AppStrings.PAM_SEGMENTS_STATUS_PAUSED, value: "paused" },
  { label: AppStrings.PAM_SEGMENTS_STATUS_ARCHIVED, value: "archived" },
];

// ─── Result params schema ─────────────────────────────────────────────────────

/**
 * Known required and optional params per result type_key.
 * Used to render the right inputs when a user picks a result in the wizard.
 * The backend validates result_type automatically — we derive it from the catalog.
 */
export type SportsbookSearchType = "tournament" | "match" | "market";

export type ResultParamField = {
  key: string;
  label: string;
  required: boolean;
  placeholder?: string;
  type:
    | "string"
    | "number"
    | "select"
    | "free-spin-provider"
    | "free-spin-games"
    | "client-currency"
    | "futures-sport"
    | "futures-league"
    | "futures-trait"
    | "datetime"
    | "readonly";
  options?: { label: string; value: string }[];
  /** Prefilled value for the form field when the result type is selected. */
  default?: string;
  /**
   * Inclusive whole-number bounds, enforced as a form rule. The backend
   * rejects out-of-range values with a 422 (e.g. split_count, BONENG-883);
   * validating here surfaces the error on the field instead of a toast.
   */
  min?: number;
  max?: number;
  /**
   * Specialized client-side format validator. `american_odds` accepts a
   * whole number with |value| ≥ 100 (e.g. 150, -110) — the existing
   * min/max pair cannot express the forbidden -99..99 band (BONENG-896).
   * `percent` accepts a number greater than 0 and at most 100, decimals
   * allowed (BONENG-913) — mirrors the backend's percent_of_trigger guard.
   * `money` accepts a positive dollar amount of at least 0.01 (BONENG-976)
   * — mirrors the backend's max_amount guard. `positive_amount` accepts a
   * $ amount greater than 0, decimals allowed (BONENG-991) — the
   * whole-number min/max pair cannot express e.g. 5.50.
   */
  format?:
    | "american_odds"
    | "percent"
    | "money"
    | "positive_amount"
    | "not_before_settlement";
  /** Shown on the label info icon (antd Form.Item `tooltip`). */
  help?: string;
  /**
   * BONENG-913 — "% of trigger amount" reward sizing. `percentOfTrigger`
   * marks the percent field itself: it only renders when the step's trigger
   * event carries a monetary amount (`PERCENT_OF_TRIGGER_EVENT_KEYS`) and the
   * `percentOfTriggerRewards` feature flag is on. `replacedByPercent` marks
   * the fixed-$ field the percent replaces — the two are mutually exclusive
   * ("% INSTEAD OF a $ amount"; the backend 422s configs carrying both), so
   * the $ field's required rule relaxes while the percent is filled in.
   */
  percentOfTrigger?: boolean;
  /**
   * BONENG-1171 — plain-language sentence rendered under the percent input in
   * the sizing selector, naming what the percentage is taken from for this
   * result type. Distinct from `help`, which stays on the label's info icon.
   */
  percentHint?: string;
  /**
   * Require this field when a sibling result_param matches. Used for
   * ThreeOaks ``value`` (dollars) which is only mandatory when
   * ``provider === "threeoaks"``. Also implies grid visibility for that sibling.
   */
  requiredWhen?: { key: string; equals: string };
  /**
   * Show this field on the grid when a sibling matches (optional params).
   * ThreeOaks ``type`` uses this so it is hidden for other providers.
   */
  visibleWhen?: { key: string; equals: string };
  replacedByPercent?: boolean;
  /**
   * BONENG-976 — "Max amount" cap on a percent-of-trigger reward. Renders
   * next to the percent input (same visibility rules) but stays disabled
   * until a percent is set, and is cleared/stripped when the percent is
   * removed — the cap is meaningless without a percent to cap.
   */
  percentCompanion?: boolean;
  /**
   * Optional params that belong in the Conditions column (grid inputs) rather
   * than restriction chips — e.g. `split_count` on award_free_picks (BONENG-883).
   */
  conditionsParam?: boolean;
  /**
   * BONENG-980 — marks this field as a sportsbook searchable restriction.
   * Renders as a searchable dropdown with chips instead of a text input.
   * The value stored on the form is still a CSV string of IDs.
   */
  sportsbookSearchType?: SportsbookSearchType;
};

/** Param key for percent-of-trigger reward sizing (BONENG-913). */
export const PERCENT_OF_TRIGGER_PARAM_KEY = "percent_of_trigger";

/** Param key for the max-amount cap on percent-of-trigger rewards (BONENG-976). */
export const MAX_AMOUNT_PARAM_KEY = "max_amount";

/**
 * Legacy sportsbook step params removed from authoring (BONENG-912). Expiration
 * lives at the campaign schedule; rollover was already dropped by BONENG-883.
 * Stripped from submit payloads so edit-resave of stored campaigns sheds them.
 */
export const REMOVED_STEP_PARAM_KEYS = [
  "rollover",
  "expiry_days",
  "expires_in_hours",
] as const;

/** Sportsbook result types that scrub REMOVED_STEP_PARAM_KEYS on submit. */
export const REMOVED_STEP_PARAM_RESULT_KEYS: ReadonlySet<string> = new Set([
  "award_free_picks",
  "award_bonus_bets",
  "create_profit_boost_promotion",
  "refund_on_loss",
]);

/**
 * Trigger events whose payload carries the monetary amount the backend's
 * percent_of_trigger computation reads (`amount` for deposits/purchases/tax,
 * `bet_amount` for sportsbook bets). On bet events leftover `amount` must not
 * beat the original stake (BONENG-1286). The ticket's "Deposit" and
 * "Sportsbook Bet Selection" triggers, tax paid (BONENG-1029), plus the
 * bet-settlement events the 2nd-step presets fire on (No Sweat Bet, Parlay
 * Insurance). Events outside this set (e.g. signup, free-bet placement) have
 * no base amount, so the percent input is hidden and any stale value is
 * stripped at submit.
 */
export const PERCENT_OF_TRIGGER_EVENT_KEYS: ReadonlySet<string> = new Set([
  // Deposit / purchase / tax (Payments)
  "makes_purchase",
  "first_time_deposit",
  "tax_paid",
  // Sportsbook bet placement
  "bet_placed",
  "placed_live_bet",
  "places_bet_rebet_cash",
  "places_profit_boost_bet",
  // Sportsbook settlement (2nd-step awards: No Sweat Bet, Parlay Insurance)
  "settled_cash_bet",
  "settled_sweat_bet",
  "settled_profit_boost_bet",
  "parlay_loss_by_one",
]);

const percentOfTriggerField = (
  help: string,
  percentHint: string,
): ResultParamField => ({
  key: PERCENT_OF_TRIGGER_PARAM_KEY,
  label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_PERCENT_OF_TRIGGER_AMOUNT,
  required: false,
  placeholder: "e.g. 30",
  type: "number",
  format: "percent",
  percentOfTrigger: true,
  help,
  percentHint,
});

/**
 * Sportsbook selection restrictions (BONENG-981). Authored as CSV strings;
 * the backend coerces CSV or list (same pattern as award_free_spins game_ids).
 * Shared by show_bonus, award_free_picks, and create_profit_boost_promotion —
 * optional, non-conditionsParam so they land in the Restrictions chips.
 */
export const sportsbookRestrictionParamFields = (): ResultParamField[] => [
  {
    key: "promo_tournament_ids",
    label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_TOURNAMENT_IDS,
    required: false,
    type: "string",
    sportsbookSearchType: "tournament",
  },
  {
    key: "promo_match_ids",
    label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MATCH_IDS,
    required: false,
    type: "string",
    sportsbookSearchType: "match",
  },
  {
    key: "promo_market_ids",
    label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MARKET_IDS,
    required: false,
    type: "string",
    sportsbookSearchType: "market",
  },
];

/** Param key for the transaction-history title (BONENG-960). */
export const TRANSACTION_TITLE_PARAM_KEY = "transaction_title_key";

/**
 * Default transaction-history title per ledger-writing result type (BONENG-960,
 * reversing BONENG-887's removal). The award defaults match the English text
 * behind the backend's `FREE_CASH_OBTAINED` / `FREE_PICK_OBTAINED` TCM strings,
 * so a campaign the admin leaves untouched reads the same as before.
 *
 * Result types absent from this map get no input: `award_free_spins`,
 * `show_bonus`, and `create_profit_boost_promotion` write no ledger row, so a
 * title there would author a param the backend never reads.
 */
export const DEFAULT_TRANSACTION_TITLES: Readonly<Record<string, string>> = {
  award_free_cash: "Free Cash Obtained",
  award_free_picks: "Free Pick Obtained",
  refund_on_loss: "Refund on loss",
  // BONENG-788: credit_wallet ledger default (backend falls back to localized
  // BONUS_OBTAINED when the param is omitted as untouched).
  credit_wallet: "Bonus Obtained",
};

/**
 * True when the operator left the prefilled title as-is. Those campaigns submit
 * without the param so the backend resolves its localized TCM string instead of
 * PAM's English literal — only wording the operator actually typed travels as a
 * literal, which is unavoidable for free text.
 */
export const isDefaultTransactionTitle = (
  resultTypeKey: string,
  value: unknown,
): boolean =>
  typeof value === "string" &&
  value.trim() === DEFAULT_TRANSACTION_TITLES[resultTypeKey];

/**
 * Optional, but `conditionsParam` keeps it a plain grid input: a prefilled
 * restriction chip renders read-only, so editing the title would mean removing
 * the chip and re-adding it from the Add Restriction menu.
 */
const transactionTitleField = (defaultTitle: string): ResultParamField => ({
  key: TRANSACTION_TITLE_PARAM_KEY,
  label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_TRANSACTION_TITLE,
  required: false,
  conditionsParam: true,
  placeholder: defaultTitle,
  type: "string",
  default: defaultTitle,
  help: AppStrings.PAM_DYNAMIC_BONUS_PARAM_TRANSACTION_TITLE_HELP,
});

// BONENG-976: optional cap on the percent-of-trigger reward (award =
// min(% of trigger, cap)). Offered only alongside percent sizing.
const maxAmountField = (): ResultParamField => ({
  key: MAX_AMOUNT_PARAM_KEY,
  label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MAX_AMOUNT,
  required: false,
  placeholder: "e.g. 10.00",
  type: "number",
  format: "money",
  percentCompanion: true,
  help: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MAX_AMOUNT_HELP,
});

/**
 * Minimum bet amount restriction (BONENG-991). Optional Restrictions chip on
 * show_bonus, award_free_picks, and create_profit_boost_promotion — the stake
 * floor counterpart to `max_wager_limit`, enforced on the sportsbook betslip.
 */
export const minBetAmountParamField = (): ResultParamField => ({
  key: "min_bet_amount",
  label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_BET_AMOUNT,
  required: false,
  placeholder: "e.g. 5.00",
  type: "number",
  format: "positive_amount",
  help: "Minimum stake ($) required for this bonus to be usable on the betslip.",
});

/**
 * Optional sportsbook selection/stake restrictions shared by show_bonus and
 * create_profit_boost_promotion (BONENG-896, BONENG-981, BONENG-991).
 * Boost Percentage is intentionally excluded — it belongs on Profit Boost only
 * (BONENG-1169).
 */
export const sportsbookOfferRestrictionFields = (): ResultParamField[] => {
  const minBet = minBetAmountParamField();
  const sportsbook = sportsbookRestrictionParamFields();
  return [
    {
      key: "min_legs",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_LEGS,
      required: false,
      placeholder: "e.g. 3",
      type: "number",
      min: 1,
    },
    {
      key: "min_odds",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_ODDS_AMERICAN,
      required: false,
      placeholder: "e.g. 150",
      type: "number",
      format: "american_odds",
      help: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_ODDS_HELP,
    },
    {
      key: "max_wager_limit",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_WAGER_CAP,
      required: false,
      placeholder: AppStrings.PAM_DYNAMIC_BONUS_PARAM_WAGER_CAP_PLACEHOLDER,
      type: "number",
      format: "positive_amount",
    },
    minBet,
    ...sportsbook,
  ];
};

const walletOptions = (): Array<{ label: string; value: string }> => [
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_WALLET_REBET_FREE_CASH,
    value: "rebet_free_cash",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASINO_CASH,
    value: "casino_cash",
  },
];

const resultParamSchema = (): Record<string, ResultParamField[]> => {
  const minBet = minBetAmountParamField();
  const sportsbook = sportsbookRestrictionParamFields();
  const offerRestrictions = sportsbookOfferRestrictionFields();
  return {
  // Optional OFFER targeting for show_bonus (BONENG-896). Banner copy still
  // comes from StepBannerEditor (`banner_title_key`/`banner_msg_key` are
  // injected on submit) — do not add those keys here.
  // Boost Percentage is Profit Boost–only (BONENG-1169); these are selection/
  // stake restrictions only.
  show_bonus: [...offerRestrictions],
  award_free_spins: [
    { key: "provider",   label: "Provider",   required: true,  type: "free-spin-provider" },
    { key: "spin_count", label: "Spin count / rounds", required: true, placeholder: "e.g. 10 (ThreeOaks: rounds)", type: "number", min: 1 },
    { key: "game_ids",   label: "Game IDs",   required: true,  type: "free-spin-games" },
    { key: "bet_level",  label: "Bet level (BGaming / Alea)", required: false, placeholder: "1", type: "number", min: 1 },
    {
      key: "value",
      label: "Value dollars (ThreeOaks)",
      required: false,
      requiredWhen: { key: "provider", equals: "threeoaks" },
      placeholder: "e.g. 1.00",
      type: "number",
      format: "money",
      help: "Required for ThreeOaks. Dollar amount converted to cents on the wire.",
    },
    {
      key: "type",
      label: "ThreeOaks type",
      required: false,
      visibleWhen: { key: "provider", equals: "threeoaks" },
      type: "select",
      options: [
        { label: "fixed", value: "fixed" },
        { label: "flexible", value: "flexible" },
        { label: "feature_freespins", value: "feature_freespins" },
      ],
      help: "ThreeOaks freebet type. Defaults to fixed on the wire when omitted.",
    },
    { key: "period_days", label: "Period (days)", required: false, placeholder: "14", type: "number", min: 1 },
    {
      key: "currency",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_CURRENCY,
      required: true,
      type: "client-currency",
      help: AppStrings.PAM_DYNAMIC_BONUS_PARAM_CURRENCY_HELP,
    },
    { key: "total_bet",  label: "Total Bet",  required: false, placeholder: "1",                type: "string" },
  ],
  award_free_cash: [
    {
      key: "amount",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_AMOUNT,
      required: true,
      placeholder: "e.g. 25.00",
      type: "string",
      format: "positive_amount",
      replacedByPercent: true,
    },
    percentOfTriggerField(
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HELP_FREE_CASH,
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HINT_FREE_CASH,
    ),
    maxAmountField(),
    transactionTitleField(DEFAULT_TRANSACTION_TITLES.award_free_cash),
  ],
  award_free_picks: [
    {
      key: "amount",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_AMOUNT,
      required: true,
      placeholder: "e.g. 25.00",
      type: "string",
      format: "positive_amount",
      replacedByPercent: true,
    },
    percentOfTriggerField(
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HELP_FREE_PICKS,
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HINT_FREE_PICKS,
    ),
    maxAmountField(),
    {
      key: "split_count",
      label: "Split Count",
      required: false,
      conditionsParam: true,
      placeholder: "e.g. 4",
      type: "number",
      min: 1,
      max: 25,
      help: "Splits the amount into N unit-valued picks issued together (Bet and Get). Leave blank to issue one regular free pick.",
    },
    { key: "min_legs", label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_LEGS, required: false, placeholder: "e.g. 3", type: "number", min: 1 },
    {
      key: "min_odds",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_ODDS_AMERICAN,
      required: false,
      placeholder: "e.g. 150",
      type: "number",
      format: "american_odds",
      help: AppStrings.PAM_DYNAMIC_BONUS_PARAM_MIN_ODDS_HELP,
    },
    transactionTitleField(DEFAULT_TRANSACTION_TITLES.award_free_picks),
    minBet,
    ...sportsbook,
  ],
  create_profit_boost_promotion: [
    {
      key: "percentage",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_BOOST_PERCENTAGE,
      required: true,
      placeholder: "e.g. 25",
      type: "number",
      format: "percent",
    },
    ...offerRestrictions,
  ],
  refund_on_loss: [
    {
      key: "max_refund_amount",
      label: "Maximum refund amount",
      required: true,
      placeholder: "e.g. 50.00",
      type: "string",
      format: "positive_amount",
      replacedByPercent: true,
    },
    percentOfTriggerField(
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HELP_REFUND,
      AppStrings.PAM_DYNAMIC_BONUS_PERCENT_HINT_REFUND,
    ),
    {
      key: "wallet",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_WALLET,
      required: false,
      type: "select",
      default: "rebet_free_cash",
      options: walletOptions(),
    },
    transactionTitleField(DEFAULT_TRANSACTION_TITLES.refund_on_loss),
  ],
  // BONENG-788: Casino Cash / wallet credit — authorable from the Result Select
  // once a schema exists (isAuthorable requires RESULT_PARAM_SCHEMA for AWARDs).
  credit_wallet: [
    // Backend CreditWallet accepts fixed amount only — no percent_of_trigger
    // (unlike award_free_cash / award_free_picks / refund_on_loss).
    { key: "amount", label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_AMOUNT, required: true, placeholder: "e.g. 25.00", type: "string", format: "positive_amount" },
    {
      key: "wallet",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_WALLET,
      required: false,
      type: "select",
      default: "casino_cash",
      options: [
        { label: AppStrings.PAM_DYNAMIC_BONUS_RESULT_CASINO_CASH, value: "casino_cash" },
        { label: AppStrings.PAM_DYNAMIC_BONUS_WALLET_REBET_FREE_CASH, value: "rebet_free_cash" },
      ],
    },
    transactionTitleField(DEFAULT_TRANSACTION_TITLES.credit_wallet),
  ],
  // BONENG-432: Cashback Offers — percentage of period net loss, evaluated
  // on the matching schedule:cashback_period_end_{weekly|monthly} trigger.
  award_cashback: [
    {
      key: "cashback_percentage",
      label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERCENTAGE,
      required: true,
      placeholder: "e.g. 10",
      type: "number",
      format: "percent",
      conditionsParam: true,
      help: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERCENTAGE_HELP,
    },
    {
      key: "calculation_period",
      label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERIOD,
      required: true,
      type: "select",
      default: "weekly",
      conditionsParam: true,
      options: [
        {
          label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERIOD_WEEKLY,
          value: "weekly",
        },
        {
          label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERIOD_MONTHLY,
          value: "monthly",
        },
      ],
      help: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_PERIOD_HELP,
    },
    {
      key: "min_loss_threshold",
      label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_MIN_LOSS,
      required: false,
      placeholder: "e.g. 50.00",
      type: "number",
      format: "money",
      conditionsParam: true,
      help: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_MIN_LOSS_HELP,
    },
    {
      key: "max_cashback_amount",
      label: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_MAX_AMOUNT,
      required: false,
      placeholder: "e.g. 15.00",
      type: "number",
      format: "money",
      conditionsParam: true,
      help: AppStrings.PAM_DYNAMIC_BONUS_CASHBACK_MAX_AMOUNT_HELP,
    },
  ],
  // BONENG-441: Future Bonus — league + trait (no Match/Market IDs). Hidden
  // until bonus.future_bonuses_pam is on (isAuthorable also requires the flag).
  award_future_bonus: [
    {
      key: "sport_id",
      label: "Sport",
      required: true,
      conditionsParam: true,
      type: "futures-sport",
      help: "Filter leagues to a sport that already has futures markets.",
    },
    {
      key: "league_id",
      label: "League",
      required: true,
      conditionsParam: true,
      type: "futures-league",
      help: "Sportsbook futures league — not a Match ID.",
    },
    {
      key: "trait_id",
      label: "Trait / stat",
      required: true,
      conditionsParam: true,
      type: "futures-trait",
      help: "League-level futures trait (season winner, MVP, top scorer, …).",
    },
    {
      key: "free_play_cash",
      label: AppStrings.PAM_DYNAMIC_BONUS_PARAM_AMOUNT,
      required: true,
      conditionsParam: true,
      placeholder: "e.g. 25.00",
      type: "string",
      format: "money",
    },
    {
      key: "expires_at",
      label: "Expiry",
      required: false,
      conditionsParam: true,
      type: "datetime",
      format: "not_before_settlement",
      help: "Leave blank to expire at the trait's expected settlement date when the bonus is granted.",
    },
    {
      key: "market_settlement_date",
      label: "Expected settlement",
      required: true,
      conditionsParam: true,
      type: "readonly",
      help: "Resolved from Sportsbook when a trait is selected. Required to save.",
    },
  ],
  };
};

export const RESULT_PARAM_SCHEMA: Record<string, ResultParamField[]> = new Proxy(
  {} as Record<string, ResultParamField[]>,
  {
    get(_target, prop) {
      if (typeof prop !== "string") return undefined;
      return resultParamSchema()[prop];
    },
  },
);

export const REFUND_ON_LOSS_RESULT_TYPE_KEY = "refund_on_loss";
export const REFUND_ON_LOSS_WALLET_PARAM_KEY = "wallet";
/** BONENG-1236: persisted when Credit Wallet [cash] is authored (flag on). */
export const PRIMARY_CASH_WALLET = "rebet_cash";
export const LEGACY_REFUND_PROMO_WALLETS: ReadonlySet<string> = new Set([
  "rebet_free_cash",
  "casino_cash",
]);

export const isLegacyRefundPromoWallet = (wallet: unknown): boolean =>
  typeof wallet === "string" && LEGACY_REFUND_PROMO_WALLETS.has(wallet);

export const isOmittedRefundWallet = (wallet: unknown): boolean =>
  wallet == null || (typeof wallet === "string" && wallet.trim() === "");

export const isCreditWalletCashWallet = (wallet: unknown): boolean =>
  wallet === PRIMARY_CASH_WALLET;

/** Authoring schema for a result. Flag-on hides the refund wallet Restriction. */
export const resultParamsForAuthoring = (
  typeKey: string,
  options?: { hideRefundWallet?: boolean },
): ResultParamField[] => {
  const fields = RESULT_PARAM_SCHEMA[typeKey] ?? [];
  if (
    options?.hideRefundWallet &&
    typeKey === REFUND_ON_LOSS_RESULT_TYPE_KEY
  ) {
    return fields.filter((field) => field.key !== REFUND_ON_LOSS_WALLET_PARAM_KEY);
  }
  return fields;
};

export const MIN_BET_AMOUNT_PARAM_FIELD = minBetAmountParamField;
export const SPORTSBOOK_RESTRICTION_PARAM_FIELDS = sportsbookRestrictionParamFields;
